import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  UseRegistermutation,
  useDropdown,
  UsecitiesDropdowns,
  useStatesDropdowns,
} from "../../actions/authActions";
import textWeight from "../../utils/textWeight";

// Helper function to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

// Helper function to get cropped image
async function getCroppedImg(imageSrc, pixelCrop, cropType = "default") {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true, alpha: true });

  // Determine export size based on crop type
  let exportWidth = pixelCrop.width;
  let exportHeight = pixelCrop.height;

  // Force Vote to Win crop to 650x866
  if (cropType === "voteToWin") {
    exportWidth = 530;
    exportHeight = 715;
  }

  canvas.width = exportWidth;
  canvas.height = exportHeight;

  // Clear and draw cropped area at correct scale
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    exportWidth,
    exportHeight
  );

  // Preserve PNG transparency if applicable
  const mimeType = imageSrc.startsWith("data:image/png")
    ? "image/png"
    : "image/jpeg";

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, 0.95); // 95% quality for JPEG
  });
}

export default () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingToapi, setLoadingToapi] = useState(false);
  const [keepPrivate, setKeepPrivate] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [cca2, setCca2] = useState(null);
  const [apiMessage, setApiMessage] = useState(null); // For showing API response message
  const location = useLocation();
  const [isDragging, setIsDragging] = useState(false);
  
  // Cropper state for Vote to Win image
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  const { TextWeight } = textWeight();

  const { data: dropdowns, isLoading: isDropdownLoading, isError, error } = useDropdown();

  // const handleFileChange = (e) => {
  //   const { name, files: selectedFiles } = e.target;
    
  //   if (selectedFiles && selectedFiles[0]) {
  //     const file = selectedFiles[0];
      
  //     console.log("File selected:", file.name, file.size, file.type); // Debug
      
  //     // Validate file type
  //     if (!file.type.startsWith('image/')) {
  //       alert('Please upload an image file');
  //       return;
  //     }
      
  //     // Validate file size (max 5MB)
  //     if (file.size > 5 * 1024 * 1024) {
  //       alert('File size must be less than 5MB');
  //       return;
  //     }
      
  //     // Update files state
  //     setFiles(prev => ({
  //       ...prev,
  //       [name]: file
  //     }));
      
  //     // Update the selected state
  //     if (name === 'voteToWinImage') {
  //       console.log("Setting isVoteToWinImageSelected to true"); // Debug
  //       setIsVoteToWinImageSelected(true);
  //     }
  //   }
  // };

  // const clearFileChange = (name) => {
  //   setFiles((prevFiles) => ({
  //     ...prevFiles,
  //     [name]: null,
  //   }));
    
  //   // Clear the selected state when clearing vote to win image
  //   if (name === 'voteToWinImage') {
  //     console.log("Setting isVoteToWinImageSelected to false"); // Debug
  //     setIsVoteToWinImageSelected(false);
  //   }
  // };

  // Cropper callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle image selection - open cropper
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert("Image size must be less than 2MB");
        e.target.value = '';
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle crop save
  const handleCropSave = async () => {
    try {
      if (!imageToCrop || !croppedAreaPixels) return;
      
      // Get cropped image blob
      const croppedBlob = await getCroppedImg(
        imageToCrop,
        croppedAreaPixels,
        'voteToWin' // Use Vote to Win dimensions
      );
      
      // Convert blob to File
      const croppedFile = new File(
        [croppedBlob],
        `vote_to_win_${Date.now()}.jpg`,
        { type: "image/jpeg" }
      );
      
      // Store in form
      setValue("voteToWinImage", croppedFile, { shouldValidate: true });
      
      // Close cropper
      setCropperOpen(false);
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      console.error("Error cropping image:", error);
      alert("Failed to crop image. Please try again.");
    }
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setCropperOpen(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith("image/")) {
      // Validate file size
      if (droppedFile.size > 2 * 1024 * 1024) {
        alert("Image size must be less than 2MB");
        return;
      }
      
      // Open cropper with dropped image
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropperOpen(true);
      };
      reader.readAsDataURL(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const queryParams = new URLSearchParams(location.search);

  const [select, setSelect] = useState({
    country: null,
    states: null,
    city: null,
  });
  const [paramRef, setParamRef] = useState(null);
  const [paramEmail, setParamEmail] = useState(null);

  useEffect(() => {
    const ref = queryParams.get("ref");
    const email = queryParams.get("email");
    if (ref) {
      setParamRef(`${ref}`);
      setParamEmail(`${encodeURIComponent(email)}`);
    }
  }, [location.search]);
  const {
    register,
    handleSubmit,
    setValue,
    control,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      mobile_num: "",
      email: "",
      countries: "",
      password: "",
      dob: "",        // Date of Birth
      gender_id: "",     // Gender
      usernameType: "email",  // Email/Phone Radio
      termsAndConditions: false,
      agreeOnVoteToWin: false,
      voteToWinImage: null, // ✅ NEW FIELD
      // referral_code: "",
    },
  });

  

  const usernameType = watch("usernameType");
  useEffect(() => {
  if (usernameType === "phone") {
    setValue("email", "");
  }
}, [usernameType, setValue]);
  const [isVoteToWinRequired, setIsVoteToWinRequired] = useState(false);
  const voteToWinImage = watch("voteToWinImage") || null;
  const isVoteToWinImageSelected = !!voteToWinImage;
  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    
    setIsVoteToWinRequired(isChecked);
    setValue("agreeOnVoteToWin", isChecked); // Make sure react-hook-form knows about this
    
    if (!isChecked) {
      setValue("voteToWinImage", null); // ✅ clear the image from form
    }
  };
  // const isVoteToWinRequired = watch("agreeOnVoteToWin");
  // const [isVoteToWinImageSelected, setIsVoteToWinImageSelected] = useState(false);
  const handleDropdwonSelect = (name, value) => {
    setSelect((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  // const { data: dropdowns, isLoading } = useDropdown();
  const selectedCountry = watch("country_id");
  const selectedState = watch("state_id");
  const { data: states, isLoading: isLoadingStates } =
    useStatesDropdowns(selectedCountry);
  const { data: cities, isLoading: citiesisLoading } =
    UsecitiesDropdowns(selectedState);
  const mutation = UseRegistermutation(
    setLoadingToapi,
    navigate,
    keepPrivate,
    getValues,
    dispatch,
    paramRef,
    paramEmail,
    setApiMessage
  );

  const onSubmit = (data, e) => {
    
    e.preventDefault();
    setLoadingToapi(true);
    
    try {
      const playloadData = new FormData();
      const formattedDob = data.dob
      ? new Date(data.dob).toISOString().split("T")[0] // gives 2025-10-22
      : "";
      
      // Extract dial code and phone number
      let phoneNumber = data.mobile_num || "";
      let dialCode = countryCode;
      
    // If phone number contains a '+' at the start, extract the dial code
    if (phoneNumber.startsWith('+')) {
      // Extract dial code (e.g., "+92 3192973923" -> dialCode: "+92", phoneNumber: "3192973923")
      const match = phoneNumber.match(/^(\+\d+)\s*(.+)$/);
      if (match) {
        dialCode = match[1]; // e.g., "+92"
        phoneNumber = match[2]; // e.g., "3192973923"
      }
    }
    
    playloadData.append("email", data.email);
    playloadData.append("password", data.password);
    playloadData.append("password_confirmation", data.password);
    playloadData.append("first_name", data.firstName);
    playloadData.append("last_name", data.lastName);
    playloadData.append("mobile_num", phoneNumber);
    playloadData.append("country_code", dialCode);
    playloadData.append("cca2", cca2); //cca2: `${country?.country_code ?? 'US'}`,
    // playloadData.append("country_id", data.country_id);
    // playloadData.append("city_id", data.city_id);
    // playloadData.append("state_id", data.state_id);
    playloadData.append("date_of_birth", formattedDob);        // Add Date of Birth
    playloadData.append("gender_id", data.gender_id);   // Add Gender
    playloadData.append("referral_code", data.referral_code);
    playloadData.append("event_code", data.event_code);
    playloadData.append("username_type", "email"); // Add Username Type (Email/Phone)
    playloadData.append("terms_of_use", true);
    // Fix: Use the checkbox value correctly
    const agreeToVoteToWin = data.agreeOnVoteToWin === true || data.agreeOnVoteToWin === "on" ? "1" : "0";
    playloadData.append("agree_to_vote_to_win", agreeToVoteToWin);

   // ✅ Get file from react-hook-form (safer)
    const file = getValues("voteToWinImage");

    if (agreeToVoteToWin === "1" && file instanceof File) {
      playloadData.append("vote_to_win_image", file);
    } else {
    }

    // // Fix: Only append file if checkbox is checked AND file exists
    // if (agreeToVoteToWin === "1" && data.voteToWinImage instanceof File) {
    //   console.log("✅ Adding Vote To Win Image:", data.voteToWinImage.name);
    //   playloadData.append("vote_to_win_image", data.voteToWinImage);
    // } else {
    //   console.log("❌ No Vote To Win Image selected");
    // }
    
    // Debug: Log FormData contents
    for (let [key, value] of playloadData.entries()) {
      if (value instanceof File) {
      } else {
      }
    }
  
  for (let [key, value] of playloadData.entries()) {
    if (value instanceof File) {
    } else {
    }
  }
  // console.log("playloadData", playloadData)
  mutation.mutate(playloadData);
  
  } catch (error) {
    setLoadingToapi(false);
  }
  };
  // const handleKeepPrivateChange = (event) => {
  //     const isChecked = event.target.checked;
  //     setKeepPrivate(isChecked);
  // };

  return {
    TextWeight,
    handleSubmit,
    onSubmit,
    control,
    errors,
    register,
    setValue,
    setCountryCode,
    getValues,
    navigate,
    loadingToapi,
    usernameType,
    isVoteToWinRequired,
    isVoteToWinImageSelected,
    voteToWinImage,
    handleCheckboxChange,
    apiMessage,
    setApiMessage,
    isDragging,
    handleDrop,
    handleDragOver,
    setIsDragging,
    dropdowns,
    isDropdownLoading,
    isError,
    error,
    // Cropper related
    cropperOpen,
    imageToCrop,
    crop,
    zoom,
    setCrop,
    setZoom,
    onCropComplete,
    handleImageSelect,
    handleCropSave,
    handleCropCancel, 
    setCca2,
  };
};